/**
 * The action model of the matter hub (docs/ux/matter-hub.md): which of the
 * engine's answers become the primary, the secondaries and the More menu.
 * Allowed and refused come from the server's options; this file only
 * decides placement and order. Nothing here re-implements a guard.
 */

import type { Status, StatusEvent } from '@beam/core/status';
import type { TransitionOption } from '@/app/types';

type Kind = StatusEvent['kind'];

/** Forward events per status, in the order they are offered; the first allowed one is the primary. */
export const FORWARD: Record<Status, Kind[]> = {
  1: ['approve'],
  2: ['paperwork_received'],
  3: ['sorted'],
  4: ['appointment_booked', 'no_appointment_required'],
  5: ['attended'],
  6: ['doctor_submits'],
  7: ['qa_submits', 'qa_completes'],
  8: ['doctor_resubmits'],
  9: ['release', 'invoice'],
  10: ['invoice', 'close'],
  11: ['release', 'close'],
  12: [],
  13: ['resume'],
  14: ['rebook'],
  15: [],
};

/** Exceptional events shown as secondaries when the engine allows them, in this order. */
const EXCEPTIONAL: Kind[] = ['dna', 'park_awaiting_information', 'reopen'];
const NEVER_PROMOTED = new Set<Kind>(['close']);

export type ActionLayout = {
  terminal: boolean;
  primary: TransitionOption | null;
  /** Forward and exceptional units after the primary, allowed or refused. */
  secondary: TransitionOption[];
  /** The More menu: a refused park and Cancel matter. */
  more: TransitionOption[];
};

export function layoutActions(status: Status, options: TransitionOption[]): ActionLayout {
  const by = new Map(options.map((o) => [o.kind, o]));
  if (status === 15) return { terminal: true, primary: null, secondary: [], more: [] };
  const forward = FORWARD[status].map((k) => by.get(k)).filter((o): o is TransitionOption => Boolean(o));
  // Close is never promoted past a refused first event: at 11 with the payment gate shut,
  // closing an unreleased report must not become the ink button (UX review).
  const primary = forward.find((o, i) => o.allowed && (i === 0 || !NEVER_PROMOTED.has(o.kind))) ?? null;
  const secondary = forward.filter((o) => o !== primary);
  for (const k of EXCEPTIONAL) {
    const o = by.get(k);
    if (o?.allowed) secondary.push(o);
  }
  const more: TransitionOption[] = [];
  const park = by.get('park_awaiting_information');
  if (park && !park.allowed && status !== 13) more.push(park);
  const cancel = by.get('cancel');
  if (cancel) more.push(cancel);
  return { terminal: false, primary, secondary, more };
}

/** Events that key an appointment (docs/ux/clinic.md); they open the booking form. */
export const BOOKING_KINDS = new Set<Kind>(['appointment_booked', 'rebook']);

/** Events whose form asks for a reason (and, for park, items); the rest fire on click. */
export const FORM_KINDS = new Set<Kind>(['park_awaiting_information', 'dna', 'cancel', 'reopen']);
export const DESTRUCTIVE_KINDS = new Set<Kind>(['dna', 'cancel']);

export const DISMISS_LABEL: Partial<Record<Kind, string>> = {
  park_awaiting_information: 'Keep as is',
  dna: 'Keep as is',
  cancel: 'Keep case',
  reopen: 'Keep closed',
};

export const CONFIRM_LABEL: Partial<Record<Kind, string>> = {
  park_awaiting_information: 'Park case',
  dna: 'Record DNA',
  cancel: 'Cancel case',
  reopen: 'Reopen case',
};

/** The kinds the transition log shows as moves (plus the seed's "created"). */
export const TRANSITION_KINDS = new Set<string>([
  'created', 'approve', 'paperwork_received', 'sorted', 'appointment_booked', 'no_appointment_required', 'attended', 'doctor_submits',
  'qa_submits', 'doctor_resubmits', 'qa_completes', 'release', 'invoice', 'close', 'park_awaiting_information', 'resume', 'dna', 'rebook', 'cancel', 'reopen',
]);
